import { Dispatch, SetStateAction, useMemo } from "react";
import { SALES_STATUS_COLOR, getStatusLabel } from "../../data";
import { formatNumber } from "../../../../utils";
import { useCompact } from "../../../../utils/hooks/useCompact";
import { InvoiceResponse } from "../../../../types/sale.type";
import { formatDate, formatThaiFullDate } from "../../../../utils/date";
import RejectCancelLink from "../../CancelActions/RejectCancelLink";
import RevertCancelLink from "../../CancelActions/RevertCancelLink";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import StorefrontIcon from "@mui/icons-material/Storefront";
// import EditIcon from "@mui/icons-material/Edit";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
// import FileOpenOutlinedIcon from "@mui/icons-material/FileOpenOutlined";
// import CallMadeOutlinedIcon from "@mui/icons-material/CallMadeOutlined";
import InvoiceDetailsTable from "./Table";
import BillTotals from "../../BillTotals";
import ReplacedByInfo from "../../ReplacedByInfo";

interface DetailsDocumentProps {
  invoice: InvoiceResponse;
  setTriggerFetch: Dispatch<SetStateAction<number>>;
}

export default function DetailsDocument({
  invoice,
  setTriggerFetch,
}: DetailsDocumentProps) {
  const compact = useCompact();
  const address = useMemo(() => {
    return [
      invoice?.customer_address1,
      invoice?.customer_address2,
      invoice?.customer_street,
      invoice?.customer_subdistrict,
      invoice?.customer_district,
      invoice?.customer_province,
      invoice?.customer_zipcode,
    ]
      .filter(Boolean)
      .join(" ");
  }, [
    invoice?.customer_address1,
    invoice?.customer_address2,
    invoice?.customer_street,
    invoice?.customer_subdistrict,
    invoice?.customer_district,
    invoice?.customer_province,
    invoice?.customer_zipcode,
  ]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          ...(compact && { flexWrap: "wrap", gap: 1 }),
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="h6" fontWeight={600}>
            ใบเสนอราคา
          </Typography>
          <Typography fontWeight={600} color="text.secondary">
            เลขที่ {invoice.invoice_no}
          </Typography>
          <ReplacedByInfo replacedBy={invoice.replaced_by} variant="body2" />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {invoice.updated_at && (
            <Typography
              fontWeight={600}
              variant="caption"
              color="text.secondary"
            >
              แก้ไขเมื่อ {formatThaiFullDate(invoice.updated_at)}
              {/* แก้ไขครั้งที่ 1 เมื่อ {formatThaiFullDate(invoice.updated_at)} */}
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: SALES_STATUS_COLOR[invoice.status] + "1A",
              color: SALES_STATUS_COLOR[invoice.status],
              borderRadius: 4,
              p: 1,
              fontWeight: 600,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                bgcolor: SALES_STATUS_COLOR[invoice.status],
                borderRadius: "50%",
                mr: 1,
              }}
            />
            {getStatusLabel(invoice.status)}
          </Box>
          <RejectCancelLink
            uuid={invoice.uuid}
            status={invoice.status}
            setTriggerFetch={setTriggerFetch}
          />
          <RevertCancelLink
            uuid={invoice.uuid}
            status={invoice.status}
            setTriggerFetch={setTriggerFetch}
          />
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <StorefrontIcon />
        <Typography fontWeight={600} variant="body1">
          ข้อมูลลูกค้า
        </Typography>
        {/* <EditIcon
          fontSize="small"
          color="primary"
          sx={{ cursor: "pointer", ml: 2 }}
        /> */}
      </Box>
      <Grid container spacing={1}>
        <Grid size={5}>
          <Typography fontWeight={600} variant="body1" color="text.secondary">
            ร้าน
          </Typography>
        </Grid>
        <Grid size={3}>
          <Typography fontWeight={600} variant="body1" color="text.secondary">
            รหัสร้านค้า
          </Typography>
        </Grid>
        <Grid size={4}>
          <Typography fontWeight={600} variant="body1" color="text.secondary">
            วันที่ออกเอกสาร
          </Typography>
        </Grid>
        <Grid size={5}>
          <Typography fontWeight={500} variant="body1">
            {invoice.customer_name} <br />
            {address}
          </Typography>
        </Grid>
        <Grid size={3}>
          <Typography fontWeight={500} variant="body1">
            {invoice.customer_code}
          </Typography>
        </Grid>
        <Grid size={4}>
          <Typography fontWeight={500} variant="body1">
            {formatDate(invoice.invoice_date)}
          </Typography>
        </Grid>
      </Grid>
      <Divider sx={{ my: 3, ...(compact && { my: 2 }) }} />
      <Box sx={{ display: "flex", gap: 6, ...(compact && { gap: 3, flexWrap: "wrap" }) }}>
        <Box sx={{ display: "flex", gap: 1, mb: 3, ...(compact && { mb: 2 }) }}>
          <SellOutlinedIcon />
          <Typography fontWeight={600} variant="body1">
            ข้อมูลการเสียภาษี
          </Typography>
        </Box>
        {!invoice.inv_taxid ? (
          <Box sx={{ display: "flex", gap: 2 }}>
            <Typography fontWeight={500} variant="body1">
              ไม่มี
            </Typography>
            <Typography variant="body1" color="error">
              (ไม่ต้องส่ง E-Tax)
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", mb: 2 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Typography fontWeight={500} variant="body1">
                เลขที่ผู้เสียภาษี
              </Typography>
              <Typography fontWeight={500} variant="body1">
                {invoice.inv_taxid}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Typography fontWeight={500} variant="body1">
                อัตราภาษี {parseFloat(invoice.tax_rate)}%
              </Typography>
              <Typography fontWeight={500} variant="body1">
                จำนวนเงิน {formatNumber(invoice.tax_amount)} บาท
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 3, ...(compact && { mb: 2 }) }} />
      {/* <Box sx={{ display: "flex", gap: 10 }}>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <FileOpenOutlinedIcon />
          <Typography fontWeight={600} variant="body1">
            ข้อมูลเพิ่มเติม
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 0.5, cursor: "pointer" }}>
            <Typography
              fontWeight={500}
              variant="body1"
              color="link"
              sx={{ textDecoration: "underline" }}
            >
              QT123499595
            </Typography>
            <CallMadeOutlinedIcon
              sx={{ color: (theme) => theme.palette.link.main }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, cursor: "pointer" }}>
            <Typography
              fontWeight={500}
              variant="body1"
              color="link"
              sx={{ textDecoration: "underline" }}
            >
              QT123499595
            </Typography>
            <CallMadeOutlinedIcon
              sx={{ color: (theme) => theme.palette.link.main }}
            />
          </Box>
        </Box>
      </Box> */}
      {/* <Divider sx={{ my: 3 }} /> */}
      <Typography fontWeight={600} variant="body1" mb={2}>
        รายการ
      </Typography>
      <InvoiceDetailsTable invoiceDetails={invoice?.invoice_detail} />
      {invoice?.invoice_detail && invoice?.invoice_detail?.length > 0 && (
        <BillTotals
          totalAmount={invoice?.total_amount}
          discountAmount={invoice?.discount_amount}
          totalNoTax={invoice?.total_no_tax}
          taxRate={invoice?.tax_rate}
          taxAmount={invoice?.tax_amount}
          grandTotal={invoice?.grand_total}
        />
      )}
    </>
  );
}
